import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { useDropzone, type Accept } from "react-dropzone";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

import DropdownMenu from "../../../components/dropdown-menu/DropdownMenu";
import ContentTagInput from "../../../components/content-tag/ContentTagInput";
import FilterChipBar from "../../../components/filter-chip/FilterChipBar";
import ToolTip from "../../../components/tooltip/ToolTip";
import MediaPreview from "../../../components/media-preview/MediaPreview";
import MediaTranscriptionModal from "../../../components/media-preview/components/MediaTranscriptionModal";
import BulkTranscriptionModal from "../../../components/transcription/BulkTranscriptionModal";
import TranscriptionStatusCell from "../../../components/transcription/TranscriptionStatusCell";
import {
  collectTranscriptionTargets,
  isTranscribableFileType,
} from "../../../components/transcription/transcriptionTargets";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import { withExtensionFallback } from "../../../utils/fileType";
import Table, { TableColumn } from "../../../components/table/Table";
import TableAddFileRow from "../../../components/table/TableAddFileRow";
import TitleBlock from "../../../components/block/TitleBlock";
import Button from "../../../components/button/Button";
import HelpHint from "../../../components/help-hint/HelpHint";
import UploadProgressBar from "../../../components/upload-progress/UploadProgressBar";
import AuditionPlayButton from "../../../components/audio-preview/AuditionPlayButton";
import {
  useAudioAudition,
  type AuditionItem,
} from "../../../components/audio-preview/core/useAudioAudition";
import {
  useCreateProfileWorkMutation,
  useDeleteProfileWorkMutation,
  useGetProfileContentQuery,
  useReorderProfileWorksMutation,
  useUpdateProfileWorkMutation,
  useUpdateProfileWorkFilterTagsMutation,
} from "../../../app/services/creatorProfile";
import useS3Upload, { type UploadingFile } from "../../../hooks/useS3Upload";
import useTranscriptionStatuses, {
  hasTranscriptionPreview,
  type TranscriptionCellInfo,
} from "../../../hooks/useTranscriptionStatuses";
import type { ProfileWork, WorkFilterTag } from "../../../types/profile";
import {
  normalizeTagList,
  profileMediaIcon,
} from "../../../utils/profileMedia";
import WorkCategoryCell from "./WorkCategoryCell";
import WorkEditModal from "./WorkEditModal";

/** サムネ枠。音源作品では再生ボタンを重ねるため相対配置にする。 */
const ThumbBox = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: var(--soremo-background);
  border: 1px solid var(--soremo-border);
`;

/**
 * サムネ上の再生ボタン。
 *
 * サムネ画像がある行（`$onImage`）はボタンが絵を潰さないよう、ホバー中と
 * 再生中だけスクリムごと出す。画像が無い行はボタン自体がサムネの中身なので
 * 常時表示する。
 */
const PlayOverlay = styled.div<{ $onImage: boolean; $playing: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $onImage }) =>
    $onImage ? "rgba(0, 0, 0, 0.25)" : "transparent"};
  opacity: ${({ $onImage, $playing }) => (!$onImage || $playing ? 1 : 0)};
  transition: opacity 0.15s;

  tr:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

const NAME_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

const MAX_UPLOAD_FILE_SIZE = 500 * 1024 * 1024;
const MAX_UPLOAD_FILE_SIZE_MB = MAX_UPLOAD_FILE_SIZE / (1024 * 1024);
/** 作品として登録できるファイル種別。 */
const MEDIA_ACCEPT = ["audio/*", "video/*", "image/*", "application/pdf"];
/**
 * react-dropzone 形式の受付種別（OS のファイル選択ダイアログの絞り込みにも効く）。
 * `useS3Upload` の `restrictions.accept` と同じ一覧から導出して二重管理を避ける。
 */
const MEDIA_DROPZONE_ACCEPT: Accept = withExtensionFallback(
  Object.fromEntries(MEDIA_ACCEPT.map((mime) => [mime, []])),
);

/** 表の使い方（? アイコンのホバーで出す） */
const WORKS_HELP = `登録済みの作品を表で管理します。作品名・カテゴリー・タグはそのまま編集、詳細は行末の 3 点リーダーから編集できます。
音源作品はサムネの再生ボタンでその場で試聴できます。
音源・動画作品は 3 点リーダーの「文字起こし」、表の上の「一括文字起こし」からまとめて文字起こしできます。
最下部のアップロード行をクリックするか、表にファイルをドラッグ＆ドロップすると、1 ファイル＝1 作品で登録します。`;

/** 拡張子から判定する作品の既定 `content_type`（音楽以外なら空文字）。 */
const detectContentTypeFromFile = (file: File): string => {
  if (file.type.startsWith("audio/")) return "music";
  return "";
};

/** ファイル名から拡張子を除いた表示用タイトル。 */
const titleFromFileName = (name: string): string =>
  name.replace(/\.[^.]+$/, "").trim() || name;

/**
 * 作品の代表メディアが文字起こしできるなら、その fileId を返す
 * （バックエンドの横断レジストリが解決する `AlbumVariation.pk`）。
 * 画像・PDF 作品や、公開閲覧側で id を持たないメディアは null。
 */
const transcribableMediaId = (work: ProfileWork): string | null => {
  const primary = work.media[0];
  if (!primary?.id || !isTranscribableFileType(primary.type)) return null;
  return String(primary.id);
};

/** 試聴（サムネの再生ボタン）で鳴らす音源 URL。対象外の作品は null。 */
const auditionSrc = (work: ProfileWork): string | null => {
  const primary = work.media[0];
  return primary?.type === "audio" && primary.url ? primary.url : null;
};

// =====================================================================
// 行（ドラッグ可能 + 作品名インライン編集）
// =====================================================================

interface SortableWorkRowProps {
  work: ProfileWork;
  canDrag: boolean;
  /** カテゴリーの `td` を描くか。`columns` の有無と必ず同じ条件で渡すこと。 */
  showCategory: boolean;
  /** カテゴリー（フィルタータグ）マスタ。 */
  filterTags: WorkFilterTag[];
  /** 音源作品で、試聴の再生ボタンを出せるか。 */
  canAudition: boolean;
  auditionPlaying: boolean;
  onAuditionToggle: () => void;
  /** 作品メディアの文字起こしステータス / 本文プレビュー（未実行なら undefined で非表示） */
  transcriptionStatus?: TranscriptionCellInfo;
  /** 文字起こしモーダルを開く（音源・動画作品のみ渡される） */
  onTranscribe?: () => void;
  // eslint-disable-next-line no-unused-vars
  onOpenEdit: (target: ProfileWork) => void;
  // eslint-disable-next-line no-unused-vars
  onRequestDelete: (target: ProfileWork) => void;
  // eslint-disable-next-line no-unused-vars
  onRenameCommit: (target: ProfileWork, newTitle: string) => void;
  // eslint-disable-next-line no-unused-vars
  onTagsCommit: (target: ProfileWork, tags: string[]) => void;
  // eslint-disable-next-line no-unused-vars
  onFilterTagsCommit: (target: ProfileWork, ids: string[]) => void;
}

const SortableWorkRow: React.FC<SortableWorkRowProps> = ({
  work,
  canDrag,
  showCategory,
  filterTags,
  canAudition,
  auditionPlaying,
  onAuditionToggle,
  transcriptionStatus,
  onTranscribe,
  onOpenEdit,
  onRequestDelete,
  onRenameCommit,
  onTagsCommit,
  onFilterTagsCommit,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(work.id),
    disabled: !canDrag,
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(work.title);

  useEffect(() => {
    if (!isEditingName) setNameDraft(work.title);
  }, [work.title, isEditingName]);

  // タグはセル内で直接編集できる（追加/削除のたびに即保存）。
  // 反映を待たず表示に出すため、ドラフトを行内に持ちサーバー値と同期する。
  const [tagsDraft, setTagsDraft] = useState<string[]>(() =>
    normalizeTagList(work.tags),
  );
  useEffect(() => {
    setTagsDraft(normalizeTagList(work.tags));
  }, [work.tags]);

  const handleTagsChange = (next: string[]) => {
    setTagsDraft(next);
    onTagsCommit(work, next);
  };

  // カテゴリー（フィルタータグ）もタグ列と同じく、選択のたびに即保存する。
  const [filterTagIdsDraft, setFilterTagIdsDraft] = useState<string[]>(
    () => work.filter_tag_ids ?? [],
  );
  useEffect(() => {
    setFilterTagIdsDraft(work.filter_tag_ids ?? []);
  }, [work.filter_tag_ids]);

  const handleFilterTagsChange = (next: string[]) => {
    setFilterTagIdsDraft(next);
    onFilterTagsCommit(work, next);
  };

  const startEditName = () => {
    setNameDraft(work.title);
    setIsEditingName(true);
  };
  const commitName = () => {
    setIsEditingName(false);
    const trimmed = nameDraft.trim();
    if (trimmed === work.title) return;
    onRenameCommit(work, trimmed);
  };
  const cancelEditName = () => {
    setIsEditingName(false);
    setNameDraft(work.title);
  };
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditName();
    }
  };

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "var(--soremo-background)" : undefined,
  };

  const primary = work.media[0];
  const hasThumbImage = !!work.image_url;

  return (
    <tr ref={setNodeRef} style={rowStyle}>
      {/* ドラッグハンドル */}
      <td style={{ textAlign: "center" }}>
        <span
          {...attributes}
          {...listeners}
          aria-label="並び替え"
          className="material-symbols-rounded u-text-light-gray"
          style={{
            fontSize: 18,
            cursor: canDrag ? "grab" : "default",
            touchAction: "none",
            display: "inline-flex",
          }}
        >
          drag_indicator
        </span>
      </td>

      {/* サムネ。サムネ画像があれば画像、無ければメディア種別のアイコンを出し、
          音源作品はそこに試聴の再生ボタンを重ねる（画像が無い音源はアイコンを
          出さず、再生ボタンだけがサムネの中身になる）。 */}
      <td>
        <ThumbBox>
          {work.image_url && (
            <MediaPreview
              src={work.image_url}
              mediaType="image"
              sizeMode={{
                mode: "fixed-height",
                height: "48px",
                maxWidth: "48px",
              }}
              disableHoverEffect
            />
          )}
          {!hasThumbImage && !canAudition && (
            <div className="u-col-center u-w100" style={{ height: "100%" }}>
              <span
                className="material-symbols-rounded u-text-light-gray"
                style={{ fontSize: 28 }}
              >
                {primary ? profileMediaIcon(primary.type) : "image"}
              </span>
            </div>
          )}
          {canAudition && (
            <PlayOverlay $onImage={hasThumbImage} $playing={auditionPlaying}>
              <AuditionPlayButton
                playing={auditionPlaying}
                size={32}
                onToggle={onAuditionToggle}
                // 画像の上は白 + 影、無地の上は通常色でないと読めない。
                variant={hasThumbImage ? "overlay" : "plain"}
              />
            </PlayOverlay>
          )}
        </ThumbBox>
      </td>

      {/* 作品名（インライン編集） */}
      <td>
        {isEditingName ? (
          <input
            type="text"
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={handleNameKeyDown}
            style={NAME_INPUT_STYLE}
          />
        ) : (
          <div
            className="u-row u-gap4 u-items-center"
            style={{ overflow: "hidden" }}
          >
            <ToolTip description={work.title || "（無題）"} truncatedOnly>
              <div
                className="bodytext-13"
                onClick={startEditName}
                style={{
                  // 文字起こしプレビューがあるときは名前を内容幅に留め、
                  // 直後からプレビューが続くようにする（共有ライブラリと同じ）。
                  // ないときは flex: 1 でクリック編集できる領域を広く保つ。
                  flex: hasTranscriptionPreview(transcriptionStatus)
                    ? "0 1 auto"
                    : 1,
                  minWidth: 0,
                  minHeight: 22,
                  lineHeight: "22px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "text",
                }}
              >
                {work.title || "（無題）"}
              </div>
            </ToolTip>
            <TranscriptionStatusCell entry={transcriptionStatus} />
          </div>
        )}
      </td>

      {/* カテゴリー（クリックでポップオーバー、選択のたびに即保存） */}
      {showCategory && (
        <td>
          <WorkCategoryCell
            tags={filterTags}
            selectedIds={filterTagIdsDraft}
            onChange={handleFilterTagsChange}
          />
        </td>
      )}

      {/* タグ（セル内で直接編集。Enter / カンマで追加、× で削除、即保存） */}
      <td>
        <ContentTagInput
          value={tagsDraft}
          onChange={handleTagsChange}
          placeholder="タグを追加"
          variant="bare"
        />
      </td>

      {/* アクション（編集・削除は 3 点リーダーに畳む） */}
      <td className="table-cell-visible" style={{ textAlign: "center" }}>
        <div className="u-row u-justify-center">
          <DropdownMenu
            size="small"
            items={[
              {
                label: "編集（詳細）",
                icon: "edit",
                onClick: () => onOpenEdit(work),
              },
              ...(onTranscribe
                ? [
                    {
                      label: "文字起こし",
                      icon: "speech_to_text",
                      onClick: onTranscribe,
                    },
                  ]
                : []),
              {
                label: "削除",
                icon: "delete",
                onClick: () => onRequestDelete(work),
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );
};

// =====================================================================
// MySiteWorks 本体
// =====================================================================

/**
 * マイサイト「作品登録」。
 *
 * - グローバル `Table` を使った表形式の一覧（作品名・カテゴリー・タグはセル内で
 *   直接編集、詳細は鉛筆アイコン）。タグ・カテゴリーの追加/削除は即保存。
 * - 音源作品はサムネに試聴の再生ボタンを重ねる（`useAudioAudition`）。
 * - 行頭ハンドルで作品の並び替え（`reorderProfileWorks` で永続化）。
 * - フィルタータグマスタ（`work_filter_tags`、ハッシュタグとは別系統）のバーを表示。
 *   同じマスタを「カテゴリー」列で作品に割り当てる（マスタが空なら列を出さない）。
 *   「+」で追加、D&D で並び替え、× で削除（確認のうえ全作品の割当からも除去）し、
 *   `updateProfileWorkFilterTags` で全置換永続化する。チップ選択で
 *   `filter_tag_ids` によるテーブル絞り込み。公開 PROFILE ページのバーにも同じ
 *   マスタ順で反映される。ハッシュタグ（タグ列 / WorkEditModal のタグ欄）は
 *   GALLERY 検索用メタデータとして残る。
 * - アップロードは表の中で完結する。表全体がドロップゾーンで、末尾の
 *   アップロード行をクリックするとファイル選択ダイアログが開き、1 ファイル＝
 *   1 作品として順に登録する（進捗は表内の全幅行に `UploadProgressBar`）。
 *   これがあれば「＋作品を追加」は不要なため、ヘッダ右上のボタンは置かない。
 */
const MySiteWorks: React.FC = () => {
  const { data: contentResp, isLoading } = useGetProfileContentQuery();
  const [reorderWorks] = useReorderProfileWorksMutation();
  const [createWork] = useCreateProfileWorkMutation();
  const [updateWork] = useUpdateProfileWorkMutation();
  const [deleteWork, { isLoading: isDeleting }] =
    useDeleteProfileWorkMutation();
  const [updateFilterTags, { isLoading: isSavingFilterTags }] =
    useUpdateProfileWorkFilterTagsMutation();

  // ===== 作品の順序 =====
  const [localOrder, setLocalOrder] = useState<ProfileWork[]>([]);
  useEffect(() => {
    const works = contentResp?.data?.works ?? [];
    setLocalOrder([...works].sort((a, b) => a.order - b.order));
  }, [contentResp]);

  // ===== フィルタータグマスタ（ユーザースコープ、ハッシュタグとは別系統） =====
  const [localFilterTags, setLocalFilterTags] = useState<WorkFilterTag[]>([]);
  useEffect(() => {
    setLocalFilterTags(contentResp?.data?.work_filter_tags ?? []);
  }, [contentResp]);

  // 編集 / 削除 モーダル
  const [editingWork, setEditingWork] = useState<ProfileWork | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProfileWork | null>(null);
  const [removeTagTarget, setRemoveTagTarget] = useState<WorkFilterTag | null>(
    null,
  );

  // タグフィルタ（選択中のフィルタータグ id）
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  const selectedTagName = useMemo(
    () => localFilterTags.find((t) => t.id === selectedTagId)?.name ?? null,
    [localFilterTags, selectedTagId],
  );

  const visibleWorks = useMemo(() => {
    if (!selectedTagId) return localOrder;
    return localOrder.filter((w) =>
      (w.filter_tag_ids ?? []).includes(selectedTagId),
    );
  }, [localOrder, selectedTagId]);

  // ===== 試聴（音源作品のサムネの再生ボタン） =====
  // 行の ref / onClick は dnd-kit と作品名のインライン編集が使っているため、
  // `getRowProps`（roving tabindex + ↑↓ / Space）は使わずボタン操作だけにする。
  // 一覧内の同時再生は useAudioAudition が transport を 1 つ共有するため起きない。
  const auditionItems = useMemo<AuditionItem[]>(
    () =>
      visibleWorks.flatMap((w) => {
        const src = auditionSrc(w);
        return src ? [{ key: String(w.id), src }] : [];
      }),
    [visibleWorks],
  );
  const audition = useAudioAudition(auditionItems);

  // ===== 文字起こし =====
  // 作品登録の「フォルダ単位」= 表に出ている作品のメディア全件（タグで
  // 絞り込み中はその絞り込み結果が対象）。fileId は AlbumVariation.pk。
  // 単体実行モーダルの対象（作品ではなく代表メディアを指す）
  const [transcribeTarget, setTranscribeTarget] = useState<{
    fileId: string;
    fileName?: string;
  } | null>(null);
  const [showBulkTranscription, setShowBulkTranscription] = useState(false);

  const transcriptionTargets = useMemo(
    () =>
      collectTranscriptionTargets(visibleWorks, (work) => {
        const fileId = transcribableMediaId(work);
        return fileId ? { fileId, fileType: work.media[0].type } : null;
      }),
    [visibleWorks],
  );
  const transcribableFileIds = useMemo(
    () => transcriptionTargets.map((t) => t.fileId),
    [transcriptionTargets],
  );
  const transcriptionStatuses = useTranscriptionStatuses(transcribableFileIds);

  // ===== DnD センサー =====
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ===== 行ドラッグ =====
  const handleRowDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = localOrder.findIndex(
        (w) => String(w.id) === String(active.id),
      );
      const newIndex = localOrder.findIndex(
        (w) => String(w.id) === String(over.id),
      );
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(localOrder, oldIndex, newIndex);
      setLocalOrder(reordered);
      try {
        await reorderWorks(reordered.map((w) => w.id)).unwrap();
      } catch {
        toast.error("並び替えに失敗しました");
        const works = contentResp?.data?.works ?? [];
        setLocalOrder([...works].sort((a, b) => a.order - b.order));
      }
    },
    [localOrder, reorderWorks, contentResp],
  );

  // ===== フィルタータグの追加 / 並び替え / 削除（全置換 PUT で永続化） =====
  const commitFilterTags = useCallback(
    async (next: { id?: string; name: string }[], errorMessage: string) => {
      try {
        const resp = await updateFilterTags(next).unwrap();
        // 新規タグの id 採番はサーバー側で行われるため、レスポンスを真実とする。
        setLocalFilterTags(resp.data.tags);
        return true;
      } catch {
        toast.error(errorMessage);
        setLocalFilterTags(contentResp?.data?.work_filter_tags ?? []);
        return false;
      }
    },
    [updateFilterTags, contentResp],
  );

  const handleFilterTagAdd = useCallback(
    async (name: string) => {
      if (localFilterTags.some((t) => t.name === name)) {
        toast.error(`「${name}」は既にあります`);
        return;
      }
      await commitFilterTags(
        [...localFilterTags, { name }],
        "タグの追加に失敗しました",
      );
    },
    [localFilterTags, commitFilterTags],
  );

  const handleFilterTagReorder = useCallback(
    async (reordered: WorkFilterTag[]) => {
      setLocalFilterTags(reordered);
      await commitFilterTags(reordered, "タグの並び替えに失敗しました");
    },
    [commitFilterTags],
  );

  const handleFilterTagRemoveConfirmed = useCallback(async () => {
    if (!removeTagTarget) return;
    const ok = await commitFilterTags(
      localFilterTags.filter((t) => t.id !== removeTagTarget.id),
      "タグの削除に失敗しました",
    );
    if (ok) {
      if (selectedTagId === removeTagTarget.id) setSelectedTagId(null);
      toast.success(`「${removeTagTarget.name}」を削除しました`);
    }
    setRemoveTagTarget(null);
  }, [removeTagTarget, localFilterTags, commitFilterTags, selectedTagId]);

  // ===== 作品削除 =====
  const handleDeleteConfirmed = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteWork(deleteTarget.id).unwrap();
      toast.success("削除しました");
      setDeleteTarget(null);
    } catch {
      toast.error("削除に失敗しました");
    }
  }, [deleteTarget, deleteWork]);

  // ===== タイトルのインライン編集コミット =====
  const handleRenameCommit = useCallback(
    async (target: ProfileWork, newTitle: string) => {
      if (!newTitle) {
        toast.error("作品名は必須です");
        return;
      }
      try {
        await updateWork({ id: target.id, body: { title: newTitle } }).unwrap();
      } catch {
        toast.error("作品名の更新に失敗しました");
      }
    },
    [updateWork],
  );

  // ===== タグセルのインライン編集コミット（追加/削除のたびに即保存） =====
  const handleTagsCommit = useCallback(
    async (target: ProfileWork, tags: string[]) => {
      try {
        await updateWork({ id: target.id, body: { tags } }).unwrap();
      } catch {
        toast.error("タグの更新に失敗しました");
      }
    },
    [updateWork],
  );

  // ===== カテゴリーセルのコミット（フィルタータグの割当。選択のたびに即保存） =====
  const handleFilterTagsCommit = useCallback(
    async (target: ProfileWork, ids: string[]) => {
      try {
        await updateWork({
          id: target.id,
          body: { filter_tag_ids: ids },
        }).unwrap();
      } catch {
        toast.error("カテゴリーの更新に失敗しました");
      }
    },
    [updateWork],
  );

  // ===== アップロード（表へのドロップ + 末尾のアップロード行） =====
  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
    purpose: "work_media",
    skipRegister: true,
    restrictions: { accept: MEDIA_ACCEPT, maxSize: MAX_UPLOAD_FILE_SIZE },
    onValidationError: (errors) =>
      errors.forEach((e) => toast.error(e.message)),
  });

  // アップロード完了ファイルから作品レコードを作成する（初回・再試行共通の後処理）
  const registerUploadedWork = useCallback(
    async (result: UploadingFile) => {
      if (!result.fileKey) return;
      // S3 転送後の作品レコード作成もクライアント主導のため、
      // 完了するまで「処理中」として扱う（離脱を引き止める）
      await withPostProcess(async () => {
        try {
          await createWork({
            title: titleFromFileName(result.file.name),
            content_type: detectContentTypeFromFile(result.file),
            audio_keys: [result.fileKey!],
          }).unwrap();
        } catch {
          toast.error(`${result.file.name}: 登録に失敗しました`);
        }
      }, "作品を登録中");
    },
    [createWork, withPostProcess],
  );

  // 1 ファイルぶんのアップロード（S3 アップロード → 作品レコード作成）。
  // 失敗行は進捗バーに残り、行の再試行ボタン（handleRetryUpload）で再開できる。
  const uploadAndRegisterWork = useCallback(
    async (file: File) => {
      const [result] = await uploadFiles([file]);
      if (result?.status === "completed") {
        await registerUploadedWork(result);
      } else if (result?.status === "error") {
        toast.error(`${file.name}: アップロードに失敗しました。再試行できます`);
      }
    },
    [uploadFiles, registerUploadedWork],
  );

  // ドロップ / ファイル選択で受け取ったファイルを 1 件ずつ順に処理する。
  // 直列にするのは、1 ファイル＝1 作品の登録順を保ち、S3 と作品作成 API へ
  // 同時に大量のリクエストを投げないため。
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        try {
          await uploadAndRegisterWork(file);
        } catch {
          toast.error(`${file.name}: 登録に失敗しました`);
        }
      }
    },
    [uploadAndRegisterWork],
  );

  const {
    getRootProps: getFileDropRootProps,
    getInputProps: getFileDropInputProps,
    isDragActive: isFileDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop: (accepted: File[]) => {
      if (accepted.length > 0) void handleFilesSelected(accepted);
    },
    // 種別・サイズで弾かれたファイルは黙って消えると原因が分からないので通知する
    onDropRejected: (rejections) =>
      rejections.forEach((r) =>
        toast.error(
          `${r.file.name}: 対応していない形式か、${MAX_UPLOAD_FILE_SIZE_MB}MB を超えています`,
        ),
      ),
    accept: MEDIA_DROPZONE_ACCEPT,
    maxSize: MAX_UPLOAD_FILE_SIZE,
    multiple: true,
    // 表全体をクリック可能にすると行のクリック編集と競合するため、
    // ファイル選択ダイアログは末尾のアップロード行からのみ開く。
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
  });

  // エラー行の再試行。アップロード成功後に作品レコード作成まで行う
  const handleRetryUpload = useCallback(
    async (file: UploadingFile) => {
      const result = await retryFile(file.id);
      if (result?.status === "completed") {
        await registerUploadedWork(result);
      }
    },
    [retryFile, registerUploadedWork],
  );

  // 全件完了したら少し待って進捗表示をクリア（成功した行は ✓ で見せたまま、
  // 落ち着いたら片付ける）。
  useEffect(() => {
    if (
      !isUploading &&
      uploadingFiles.length > 0 &&
      uploadingFiles.every((f) => f.status === "completed")
    ) {
      const t = window.setTimeout(() => clearCompleted(), 1500);
      return () => window.clearTimeout(t);
    }
  }, [isUploading, uploadingFiles, clearCompleted]);

  // フィルタ中は行 DnD を無効化（フィルタで一部しか表示しないと意図しない順序になる）。
  const canDragRows = selectedTagId === null;

  // タグマスタが空なら選ぶものが無いので、カテゴリー列そのものを出さない。
  const showCategoryColumn = localFilterTags.length > 0;

  const columns: TableColumn[] = [
    { key: "drag", width: 36, minWidth: 36, resizable: false },
    { key: "thumb", header: "", width: 64, minWidth: 56, resizable: false },
    { key: "title", header: "作品名", minWidth: 200 },
    ...(showCategoryColumn
      ? [{ key: "category", header: "カテゴリー", width: 200, minWidth: 120 }]
      : []),
    { key: "tags", header: "タグ", width: 220, minWidth: 120 },
    { key: "actions", header: "", width: 44, minWidth: 44, resizable: false },
  ];
  const columnCount = columns.length;

  return (
    <div className="u-col u-gap16">
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="作品登録" />
        <HelpHint description={WORKS_HELP} />
      </div>

      {/* フィルタータグバー（+ 追加 / D&D 並び替え / × 削除 / 選択で絞り込み） */}
      <FilterChipBar
        items={localFilterTags}
        selectedId={selectedTagId}
        onSelect={(id) => setSelectedTagId((cur) => (cur === id ? null : id))}
        onReorder={handleFilterTagReorder}
        onAdd={handleFilterTagAdd}
        onRemove={(tag) => setRemoveTagTarget(tag)}
        addPlaceholder="新しいタグ"
        wrap
      />

      <div className="u-row-between u-items-center u-w100">
        <span className="bodytext-13 u-text-light-gray">
          {selectedTagId
            ? `${visibleWorks.length}件（タグ: ${selectedTagName ?? ""}）`
            : `全${localOrder.length}件`}
        </span>
        {transcriptionTargets.length > 0 && (
          <Button
            size="small"
            theme="secondary"
            variant="text"
            width="fit"
            leftIcon="speech_to_text"
            onClick={() => setShowBulkTranscription(true)}
          >
            {`一括文字起こし（${transcriptionTargets.length}件）`}
          </Button>
        )}
      </div>

      {/* ファイルのドロップを受ける器。横スクロール・枠・余白は Table 内蔵の
          スクロール領域に任せ、ここはドロップ中のハイライトだけを担う。
          行の並び替えは dnd-kit（Pointer イベント）、ファイル投下は HTML5
          ネイティブ drag なのでイベント系統が異なり衝突しない。 */}
      <div
        className="u-w100"
        {...getFileDropRootProps()}
        style={{
          borderRadius: 4,
          ...(isFileDragActive
            ? {
                outline: "2px dashed var(--soremo-blue)",
                outlineOffset: "-2px",
                background: "rgba(0, 154, 206, 0.05)",
              }
            : {}),
        }}
      >
        <input {...getFileDropInputProps()} />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleRowDragEnd}
        >
          <SortableContext
            items={visibleWorks.map((w) => String(w.id))}
            strategy={verticalListSortingStrategy}
          >
            <Table columns={columns} minWidth={showCategoryColumn ? 880 : 680}>
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount}>
                    <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                      読み込み中...
                    </p>
                  </td>
                </tr>
              ) : (
                <>
                  {visibleWorks.length === 0 && (
                    <tr>
                      <td colSpan={columnCount}>
                        <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                          {selectedTagId
                            ? "このタグの作品はありません"
                            : "作品がありません"}
                        </p>
                      </td>
                    </tr>
                  )}
                  {visibleWorks.map((work) => {
                    const transcribeFileId = transcribableMediaId(work);
                    return (
                      <SortableWorkRow
                        key={work.id}
                        work={work}
                        canDrag={canDragRows}
                        showCategory={showCategoryColumn}
                        filterTags={localFilterTags}
                        canAudition={!!auditionSrc(work)}
                        auditionPlaying={
                          audition.playingKey === String(work.id)
                        }
                        onAuditionToggle={() =>
                          audition.toggle(String(work.id))
                        }
                        transcriptionStatus={
                          transcribeFileId
                            ? transcriptionStatuses[transcribeFileId]
                            : undefined
                        }
                        onTranscribe={
                          transcribeFileId
                            ? () =>
                                setTranscribeTarget({
                                  fileId: transcribeFileId,
                                  fileName: work.media[0]?.file_name,
                                })
                            : undefined
                        }
                        onOpenEdit={(w) => setEditingWork(w)}
                        onRequestDelete={(w) => setDeleteTarget(w)}
                        onRenameCommit={handleRenameCommit}
                        onTagsCommit={handleTagsCommit}
                        onFilterTagsCommit={handleFilterTagsCommit}
                      />
                    );
                  })}

                  {/* アップロード進捗（全幅で差し込む） */}
                  {(uploadingFiles.length > 0 || postProcessLabel) && (
                    <tr>
                      <td colSpan={columnCount}>
                        <UploadProgressBar
                          files={uploadingFiles}
                          onCancel={abortFile}
                          onRetry={(file) => void handleRetryUpload(file)}
                          compact
                          postProcessLabel={postProcessLabel}
                        />
                      </td>
                    </tr>
                  )}

                  {/* 末尾のアップロード行: 1 ファイル = 1 作品で登録する入口 */}
                  <tr>
                    <td colSpan={columnCount}>
                      <TableAddFileRow
                        onClick={openFileDialog}
                        disabled={isUploading}
                        label="クリックして作品ファイルを追加（表へのドラッグ＆ドロップも可。1 ファイル＝1 作品で登録）"
                        ariaLabel="作品ファイルを追加"
                      />
                    </td>
                  </tr>
                </>
              )}
            </Table>
          </SortableContext>
        </DndContext>
      </div>

      {/* 編集モーダル */}
      {editingWork && (
        <WorkEditModal
          work={editingWork}
          filterTags={localFilterTags}
          onClose={() => setEditingWork(null)}
        />
      )}

      {/* 文字起こし（単体。プレイヤーが無いためシークなし） */}
      {transcribeTarget && (
        <MediaTranscriptionModal
          fileId={transcribeTarget.fileId}
          fileName={transcribeTarget.fileName}
          onClose={() => setTranscribeTarget(null)}
        />
      )}

      {/* 文字起こし（表に出ている作品のメディア全件） */}
      {showBulkTranscription && (
        <BulkTranscriptionModal
          targets={transcriptionTargets}
          scopeLabel={
            selectedTagName
              ? `作品登録（タグ: ${selectedTagName}）のメディア`
              : "作品登録のメディア"
          }
          onClose={() => setShowBulkTranscription(false)}
        />
      )}

      {/* フィルタータグ削除確認 */}
      {removeTagTarget && (
        <ConfirmModal
          message={`「${removeTagTarget.name}」タグを削除しますか？`}
          confirmLabel="削除"
          loading={isSavingFilterTags}
          onConfirm={handleFilterTagRemoveConfirmed}
          onCancel={() => setRemoveTagTarget(null)}
        />
      )}

      {/* 作品削除確認 */}
      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.title || "（無題）"}」を削除しますか？\nこの操作は取り消せません。`}
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default MySiteWorks;
